import ItemCover from "components/ItemCover"
import ProductColors from "components/ProductColors"
import { type ChangeEvent } from "react"
import { useTranslation } from "react-i18next"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Table, TableBody, TableCell, TableRow } from "@/components/ui/table"
import NoItems from "../NoItems"
import PageLoader from "../PageLoader"

interface AddProductModalProps {
  isModalOpen: boolean
  setModalOpen: (isOpen: boolean) => void
  newProducts: any[]
  loadingNewProducts: boolean
  debouncedSearchText: string
  searchText: string
  setSearchText: (text: string) => void
  addProduct: (product: any, sku: any) => void
}

export const SearchProductModal = ({
  isModalOpen,
  setModalOpen,
  newProducts,
  loadingNewProducts,
  debouncedSearchText,
  searchText,
  setSearchText,
  addProduct,
}: AddProductModalProps) => {
  const { t } = useTranslation()

  return (
    <Dialog open={isModalOpen} onOpenChange={() => setModalOpen(false)}>
      <DialogContent className="!max-w-5xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{t("searchProductModal.addProductTitle")}</DialogTitle>
        </DialogHeader>
        <div className="px-4 py-3">
          <div className="flex items-center gap-2">
            <Input
              name="searchbar"
              value={searchText}
              onChange={(e: ChangeEvent<HTMLInputElement>) => setSearchText(e.target.value)}
              placeholder={t("searchProductModal.searchPlaceholder")}
            />
            {searchText && (
              <Button variant="outline" size="sm" onClick={() => setSearchText("")}>
                {t("searchProductModal.clearSearch")}
              </Button>
            )}
          </div>
          {!!newProducts.length && (
            <Table>
              <TableBody>
                {newProducts.map((product: any) => (
                  <TableRow key={product.id}>
                    <TableCell>
                      <div className="p-1">
                        <ItemCover cover={product.cover} />
                      </div>
                    </TableCell>
                    <TableCell>
                      <ProductColors
                        product={product}
                        onClick={(sku: any) => addProduct(product, sku)}
                        size={32}
                      />
                    </TableCell>
                    <TableCell className="max-w-[200px] whitespace-nowrap overflow-hidden text-ellipsis">
                      {product.name}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
          {loadingNewProducts && <PageLoader />}
          {!newProducts.length && !loadingNewProducts && debouncedSearchText !== "" && (
            <NoItems
              text={t("searchProductModal.noProductsFound")}
              addText={t("searchProductModal.createNewProduct")}
              addLink={`/products/add`}
              showAdd
            />
          )}
        </div>
        <DialogFooter>
          <Button onClick={() => setModalOpen(false)}>{t("searchProductModal.closeButton")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
